import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {useFetchLogsQuery} from '~/api/logs/LoggingService.api';
import size from 'lodash/fp/size';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import ListTableTS from '~/features/base/components/table/ListTableTS';
import {cm4LogRequestColumnDescriptors} from '~/features/deviceLogs/components/logsTable/cm4ColumnDescriptors';
import ReloadButton from '~/features/base/components/ReloadButton';
import map from 'lodash/fp/map';
import {renderTableData} from '~/features/base/components/table/Utils';
import LogFilesTable from '~/features/deviceLogs/components/logsTable/LogFilesTable';
import NoData from '~/features/base/components/NoData';
import {TableLoader} from '~/features/base/components/TableLoader';
import {LogsSearchCriteriaDeviceTypeEnum} from '~/api/models/logs-search-criteria';
import LogFilesDownloadZipButton from "~/features/deviceLogs/components/logsTable/LogFilesDownloadZipButton";
import { useSelector } from 'react-redux';
import { getCurrentlySelectedDeviceInfoSelector } from '~/features/devices/selectors/controlDeviceSelectors';
import {PersistedLogsRequest} from "~/api/models/persistedLogsRequest";

interface CM4LogsTableProps {
    serialNumber: string;
    deviceType: LogsSearchCriteriaDeviceTypeEnum;
}

const PAGE_SIZE = 50;
const CM4LogsTable: React.FC<CM4LogsTableProps> = ({serialNumber, deviceType}) => {
    const deviceInfo = useSelector(getCurrentlySelectedDeviceInfoSelector);

    const [page, setPage] = useState<number>(0);
    const {data: logsInfo, isLoading, refetch} = useFetchLogsQuery({
        serialNumber: serialNumber,
        deviceType: deviceType,
        pageNumber: page,
        pageSize: PAGE_SIZE,
    }, {refetchOnMountOrArgChange: true});
    const [openExpanders, setOpenExpander] = useState<{ [key: string]: boolean }>({});

    const handleReloadClick = () => {
        if (page === 0) refetch();
        else setPage(0);
    };
    const handleLoadMore = () => {
        setPage(page + 1);
    };

    if (isLoading) {
        const allLabels = cm4LogRequestColumnDescriptors.map(column => column.label);
        return (
            <>
                <div className={'h5 margin-bottom-20'}>
                    <span className={'h5'}><FormattedMessage id='intl-msg:logs'/></span>
                </div>
                <TableLoader labels={allLabels} nLines={4}/>
            </>);
    }

    if (!isLoading && !size(logsInfo?.content)) {
        return (
            <>
                <div className={'h5 margin-bottom-20'}>
                    <span className={'h5'}><FormattedMessage id='intl-msg:logs'/></span>
                </div>
                <NotFoundState fullWidth
                               headline={<FormattedMessage id='intl-msg:nothingFound'/>}
                               message={<div className={'display-flex'} style={{ flexDirection: 'column' }}
                               >
                                   <div className={'margin-bottom-25'}><FormattedMessage
                                       id='intl-msg:noDeviceLogsAvailable'/></div>
                                   <ReloadButton onReloadClick={handleReloadClick}/>
                               </div>}
                />

            </>
        );
    }

    const renderShowFilesButton = (operationId, id, item) => {
        const essentialImage = deviceInfo?.lastEssentialImageReported;

        const isVersion14OrHigher = essentialImage &&
            essentialImage.version &&
            essentialImage.version.major >= 14;

        if (isVersion14OrHigher && item.status !== PersistedLogsRequest.StatusEnum.UPLOADED) {
            return <NoData/>;
        }

        const isVersion12or13 = essentialImage &&
            essentialImage.version &&
            essentialImage.version.major >= 12 && essentialImage.version.major <= 13 ;

        const showWarning = isVersion12or13 && item.status === PersistedLogsRequest.StatusEnum.REQUESTED;

        return (
            <div className={'display-flex align-items-center justify-content-between w-100'}>
                <div>
                    <LogFilesDownloadZipButton showWarning={showWarning} id={id}/>
                </div>
                <div>
                    <button type='button' className='' onClick={
                        (event) => {
                            if (openExpanders[operationId] !== undefined) {
                                setOpenExpander({
                                    [operationId]: !openExpanders[operationId]
                                });
                            } else {
                                setOpenExpander({
                                    [operationId]: true
                                });
                            }
                        }}>
                        {
                            openExpanders[operationId] ?
                                <span className='uikit-icon icon-chevron-up text-size-10' aria-hidden='true'></span>
                                : <span className='uikit-icon icon-chevron-down text-size-10' aria-hidden='true'></span>
                        }
                    </button>
                </div>
            </div>
        );
    };

    const renderCustomRow = (item) => {
        const showFilesButton = item?.logs?.length > 0 ? renderShowFilesButton(item.operationId, item.id, item) : <NoData/>;
        const tableData = map(columnDescriptor => renderTableData(item, columnDescriptor), cm4LogRequestColumnDescriptors);
        if (size(tableData)) {
            tableData[5] = <td className=''>
                <div className='padding-right-25'>
                    {showFilesButton}
                </div>
            </td>;
        }
        const key = item['operationId'];
        const tableRows = [
            <tr className='tableRow' key={key} data-key={key}>
                {tableData}
            </tr>,
        ];

        openExpanders[item.operationId] ?
            tableRows.push(
                <tr key={key + '_'}>
                    <td style={{ borderTop: 0 }} colSpan={cm4LogRequestColumnDescriptors.length}>
                        {item?.logs && <LogFilesTable key={key + '__'} logsData={item.logs}/>}
                    </td>
                </tr>,
            ) : null;
        return tableRows;
    };

    const hasMoreLogs = () => {
        if (logsInfo?.totalElements && logsInfo?.content.length) {
            return logsInfo?.totalElements > logsInfo?.content.length;
        } else {
            return false;
        }
    };

    const filteredContent = logsInfo?.content?.map(item => ({
        ...item,
        logs: item.logs?.filter(log => !((!log.system && log.key?.endsWith('.zip'))))
    })) || [];

    return (
        <div className={'display-flex flex-column'}>
            <div className={'display-flex justify-content-between'}>
                <div className={'h5 margin-bottom-20'}>
                    <span className={'h5'}><FormattedMessage id='intl-msg:logs'/></span>
                </div>
                <ReloadButton onReloadClick={handleReloadClick} className={'align-self-end margin-bottom-15'}/></div>
            <div>
                <ListTableTS
                    className='table-bordered   table-layout-fixed'
                    items={filteredContent}
                    itemKey='operationId'
                    columnDescriptors={cm4LogRequestColumnDescriptors}
                    renderCustomTableRow={renderCustomRow}
                />
                <div className={'display-flex justify-content-center'}>
                    <button id='load-more-button'
                            disabled={!hasMoreLogs()}
                            className='btn btn-default'
                            onClick={handleLoadMore}>
                        <FormattedMessage id='intl-msg:loadMore'/>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CM4LogsTable;
